import axios from "axios";
import { baseUrl } from "../util";

const getConfig = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
    "Content-Type": "application/json",
  },
});

export const accessService = {
  getSharedCompanies: async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/accounts/access/companies/`,
        getConfig()
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching shared companies:", error);
      throw error;
    }
  },

  getSharedUsers: async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/accounts/access/shared-users/`,
        getConfig()
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching shared users:", error);
      throw error;
    }
  },

  getPendingAccess: async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/accounts/access/pending/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching pending access:", error);
      throw error;
    }
  },

  requestAccess: async (data) => {
    try {
      const response = await axios.post(
        `${baseUrl}/api/accounts/access/request/`,
        data,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error requesting access:", error);
      throw error;
    }
  },

  inviteUser: async (data) => {
    try {
      const response = await axios.post(
        `${baseUrl}/api/accounts/access/invite/`,
        data,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error inviting user:", error);
      throw error;
    }
  },

  respondToAccess: async (accessId, accept) => {
    try {
      const response = await axios.post(
        `${baseUrl}/api/accounts/access/respond/${accessId}/`,
        { accept },
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error responding to access:", error);
      throw error;
    }
  },

  updateUserModules: async ({
    userId,
    moduleIds,
    permission,
    bundles,
    upgradeRequestId,
    upgradeStatus,
  }) => {
    if (!userId) {
      throw new Error("userId is required");
    }

    try {
      const response = await axios.put(
        `${baseUrl}/api/accounts/access/modules/${userId}/`,
        {
          modules: moduleIds,
          permission: permission,
          bundles: bundles,
          request_id: upgradeRequestId || null,
          status: upgradeStatus || null,
        },
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error updating modules:", error);
      throw error;
    }
  },
  batchInviteUsers: async (data) => {
    try {
      const response = await axios.post(
        `${baseUrl}/api/accounts/access/batch-invite/`,
        data,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error batch inviting users:", error);
      throw error;
    }
  },

  createCheckoutSession: async (data) => {
    try {
      const response = await axios.post(
        `${baseUrl}/api/accounts/access/create-checkout-session/`,
        data,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error creating checkout session:", error);
      throw error;
    }
  },

  createRenewalSession: async (subscriptionId) => {
    try {
      const response = await axios.post(
        `${baseUrl}/api/accounts/access/create-renewal-session/${subscriptionId}/`,
        {},
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error creating renewal session:", error);
      throw error;
    }
  },

  getSubscriptionDetails: async (subscriptionId) => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/accounts/access/subscription/${subscriptionId}/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching subscription details:", error);
      throw error;
    }
  },
  getCompanyPlanSummary: async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/accounts/access/company-plan-summary/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching company plan:", error);
      throw error;
    }
  },

  getTransactionHistory: async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/accounts/access/transaction-history/`,
        getConfig()
      );
      return response.data.transactions;
    } catch (error) {
      console.error("Error fetching transaction history:", error);
      throw error;
    }
  },
  getSharedUserAccess: async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/accounts/access/shared-users-teams/`,
        getConfig()
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching transaction history:", error);
      throw error;
    }
  },
  createAccessPaymentSession: async (accessId, period) => {
    try {
      const response = await axios.post(
        `${baseUrl}/api/accounts/access/create-payment-session/${accessId}/`,
        { period },
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error creating access payment session:", error);
      throw error;
    }
  },

  getUserModules: async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/accounts/access/user-modules/`,
        getConfig()
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching user modules:", error);
      throw error;
    }
  },

  getUserSubscription: async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/accounts/subscription/self/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching user modules:", error);
      throw error;
    }
  },
  updateAccessManagerSubscription: async ({
    userId,
    moduleIds,
    permission,
    bundles,
  }) => {
    if (!userId) {
      throw new Error("userId is required");
    }

    try {
      const response = await axios.put(
        `${baseUrl}/api/accounts/subscription/update-subscription/`,
        {
          modules: moduleIds,
          permission: permission,
          bundles: bundles,
        },
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error updating modules:", error);
      throw error;
    }
  },
  createUpgradeRequest: async () => {
    try {
      const response = await axios.post(
        `${baseUrl}/api/accounts/access/upgrade-requests/`,
        {},
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error creating upgrade request:", error);
      throw error;
    }
  },

  getPendingUpgradeRequests: async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/accounts/access/upgrade-requests/pending/`,
        getConfig()
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching pending upgrade requests:", error);
      throw error;
    }
  },

  updateUpgradeRequest: async (requestId, data) => {
    try {
      const response = await axios.put(
        `${baseUrl}/api/accounts/access/upgrade-requests/${requestId}/`,
        data,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error updating upgrade request:", error);
      throw error;
    }
  },
};

export default accessService;
